import { ChangeDetectorRef, Component, computed, OnInit, signal } from '@angular/core';
import { AuthService, BreadcrumbService, EnumValues, ModalService, Note, NotesSidebarComponent, UserLite } from '@card-software/library';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { InvoiceProxyService } from '../../../proxies/order/invoice.proxy.service';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { Invoice, InvoiceSearchCommand } from '../../../models/invoice/invoice';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Shipment } from '../../../models/order/shipment';
import { ShipmentProxyService } from '../../../proxies/order/shipment.proxy.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ItemMaster } from '../../../models/item/item-master';
import { FinanceProxyService } from '../../../proxies/finance/finance.proxy.service';
import { Business, BusinessSearch } from '../../../models/finance/business';
import { LotItemProxyService } from '../../../proxies/inventory/lot-item.proxy.service';
import { LotItem } from '../../../models/inventory/lot-item';
import { ActivatedRoute, Router } from '@angular/router';
import { EnumService } from '../../../services/enum.service';
import { QualityDefect } from '../../../models/inventory/quality-defects';
import { QualityDefectProxyService } from '../../../proxies/inventory/quality-defect.proxy.service';
import { YarnQcProxyService } from '../../../proxies/inventory/yarn-qc.proxy.service';
import { ItemCodeKeyWithResult, SupportingDocument, YarnQC } from '../../../models/yarn/yarn-qc';
import { Observable, catchError, finalize, forkJoin, map, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ItemMasterWithResults } from '../../../models/yarn/yarn-qc-forms';
import { LocalHelperService } from '../../../services/local-helper.service';
import { setOptionsForPrimeNg } from '../../../utils/enum-primeng';

@Component({
  selector: 'app-yarn-qc-details',
  templateUrl: './yarn-qc-details.component.html',
  styleUrl: './yarn-qc-details.component.scss',
})
export class YarnQcDetailsComponent implements OnInit {
  isLoading = 0;
  providers = signal<Business[]>([]);
  manufacturers = signal<Business[]>([]);
  isUpdate = signal<boolean>(false);
  canEdit = computed(() => AuthService.hasRole(UserGroup.YarnQCAdmin));

  yarnQC = signal<YarnQC | null>(null);
  generalFiles = signal<SupportingDocument[]>([]);
  resultStatusTypeOptions = signal<{ label: string; value: number }[]>([]);
  // this is to handle the table

  uniqueLotGroups = computed(() =>
    Array.from(
      this.itemCodeKeyWithResults.controls.reduce((map, ctrl) => {
        const lotNumber = ctrl.get('manufacturerLotNumber')?.value;
        if (lotNumber && !map.has(lotNumber)) {
          map.set(lotNumber, ctrl);
        }
        return map;
      }, new Map<string, FormGroup<ItemMasterWithResults>>()),
    ).map(([lotNumber, group]) => ({ lotNumber, group })),
  );

  itemsMasterSelected = signal<ItemMaster[]>([]);
  lotsSelected = signal<LotItem[]>([]);
  allQualityDefects = signal<QualityDefect[]>([]);
  qualityDefectsTranslated = computed(() =>
    this.allQualityDefects().map((defect) => ({
      ...defect,
      name: this.localHelperService.getLocalizedValue(defect.name),
    })),
  );

  userLite: UserLite | null;
  notes: Note[] = [];

  //enums
  statuses: EnumValues;

  // form controls
  invoiceSelected: FormControl<Invoice | null> = new FormControl(null, { validators: Validators.required });
  shipmentSelected: FormControl<Shipment | null> = new FormControl(null, { validators: Validators.required });
  yarnToTestedSelected: FormControl<ItemMaster | null> = new FormControl(null, { validators: Validators.required });
  conditional = new FormControl('', [Validators.required]);
  status: FormControl<number | null> = new FormControl(0, [Validators.required]);

  itemCodeKeyWithResults = new FormArray<FormGroup<ItemMasterWithResults>>([]);

  constructor(
    private userAuthenticationService: UserAuthenticationService,
    private invoiceProxyService: InvoiceProxyService,
    private shipmentProxyService: ShipmentProxyService,
    private itemMasterProxyService: ItemMasterProxyService,
    private financeProxyService: FinanceProxyService,
    private lotItemProxyService: LotItemProxyService,
    private router: Router,
    private enumService: EnumService,
    private qualityDefectProxyService: QualityDefectProxyService,
    private yarnQcProxyService: YarnQcProxyService,
    private activatedRoute: ActivatedRoute,
    private ctr: ChangeDetectorRef,
    private translateService: TranslateService,
    public localHelperService: LocalHelperService,
    private modalService: ModalService,
    private breadcrumbService: BreadcrumbService,
  ) {
    this.userLite = this.userAuthenticationService.userLite;
    this.statuses = this.enumService.getEnum('RequisitionStatus', 'inventory').values;
    this.resultStatusType();

    this.invoiceSelected.valueChanges.subscribe((value) => {
      if (!value) return;
      if (!this.isUpdate()) {
        this.resetData();
        this.getLotsByInvoiceReference(value.id);
        this.getShipment(value.id);
      }
    });
  }

  ngOnInit(): void {
    this.getProviders();
    this.getManufacturers();

    this.getInitialData();
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['id'] === 'new') {
        this.isUpdate.set(false);
        this.breadcrumbService.setCurrent('Common_New');
      } else {
        this.isUpdate.set(true);
        this.getYarnQC(params['id']);
      }
    });
  }

  uploadGeneralFile(event: Event, fileInput: HTMLInputElement): void {
    const typeUpload = 0; // 0 = General YarnQC, 1 = supporting items files
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      console.warn('No file selected.');
      return;
    }

    const files = input.files;

    if (this.yarnQC() && files) {
      this.yarnQcProxyService.uploadYarnQCFiles(this.yarnQC()?.id ?? '', null, files, typeUpload).subscribe({
        next: ({ data }) => {
          if (data) {
            this.generalFiles.update((files) => [...files, ...data]);
            GlobalMessageUtil.success('Success', 'File uploaded successfully');
          }
        },
        error: (error) => {
          console.error('Error:', error);
        },
        complete: () => {
          fileInput.value = '';
        },
      });
    } else {
      fileInput.value = '';
    }
  }

  deleteGeneralFile(doc: SupportingDocument) {
    const yarnQualityUploadType = 0;
    this.yarnQcProxyService.deleteFiles(this.yarnQC()?.id ?? '', null, [doc], yarnQualityUploadType).subscribe({
      next: ({ data }) => {
        if (data) {
          this.generalFiles.update((files) => files.filter((file) => file.name !== doc.name));
          GlobalMessageUtil.success('Success', 'File deleted successfully');
        }
      },
      error: (error) => {
        console.error('Error:', error);
      },
    });
  }
  //#region initialized Data

  private getInitialData() {
    this.isLoading++;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const businessSearch: BusinessSearch = {
      name: '',
      code: '',
      businessTypes: [0],
    };

    const invoiceCommand: InvoiceSearchCommand = {
      invoiceNumber: '',
      excludeInvoicesInShipment: false,
    };

    forkJoin({
      invoices: this.invoiceProxyService.searchInvoice(invoiceCommand, 1, 9999),
      qualityDefect: this.qualityDefectProxyService.getQualitiesDefects(),
      //manufacturers: this.financeProxyService.getAllManufacturers(businessSearch),
      //providers: this.financeProxyService.getAllProviders(businessSearch),
    })
      .pipe(
        finalize(() => {
          this.isLoading--;
        }),
      )
      .subscribe({
        next: (results) => {
          //console.log(results.qualityDefect.data);

          this.invoiceOptions.set(results.invoices.data?.items ?? []);
          this.allQualityDefects.set(results.qualityDefect.data ?? []);
        },
        error: (error) => {
          console.error('Error fetching initial data:', error);
        },
      });
  }

  private getYarnQC(id: string) {
    this.isLoading++;
    this.yarnQcProxyService.getYarnQC(id).subscribe({
      next: ({ data }) => {
        if (data) {
          this.yarnQC.set(data);
          this.breadcrumbService.setCurrent(data.number);
          this.notes = data.notes ?? [];
          this.conditional.setValue(data.comments ?? '');

          this.generalFiles.set(data.supportingDocuments ?? []);

          // 🔹 Poblar todos los items del API en el FormArray
          if (data.itemCodeKeyWithResults?.length) {
            this.populateFormArray(data.itemCodeKeyWithResults);
          }

          // Cargar los masters para display
          if (data.itemCodeKeyWithResults?.length) {
            const itemCodes = data.itemCodeKeyWithResults.map((item) => item.itemCode);
            this.fetchItemMastersForDisplay(itemCodes);
          }

          // Manejar invoice
          if (data.invoiceReference) {
            const existingInvoice = this.invoiceOptions().find((invoice) => invoice.id === data.invoiceReference);
            if (existingInvoice) {
              this.invoiceSelected.setValue(existingInvoice);
              this.getShipment(existingInvoice.id);
            } else {
              this.fetchSpecificInvoice(data.invoiceReference);
            }
          }

          this.isLoading--;
        }
      },
      error: (error) => {
        console.error('error', error);
        this.isLoading--;
        this.invoiceSelected.setValue(null);
      },
    });
  }

  private fetchSpecificInvoice(invoiceId: string) {
    this.isLoading++;
    this.invoiceProxyService.getInvoice(invoiceId).subscribe({
      next: (invoiceResponse) => {
        if (invoiceResponse.data) {
          this.invoiceSelected.setValue(invoiceResponse.data);

          this.getShipment(invoiceResponse.data.id);

          // Add to options for future reference if not already there
          if (!this.invoiceOptions().some((inv) => inv.id === invoiceResponse.data!.id)) {
            this.invoiceOptions.update((options) => [...options, invoiceResponse.data!]);
          }
        }
        this.isLoading--;
      },
      error: (error) => {
        console.error('Error fetching specific invoice:', error);
        this.isLoading--;
      },
    });
  }

  private fetchItemMastersForDisplay(itemCodes: string[]) {
    this.itemMasterProxyService.getByItemCodes(itemCodes).subscribe({
      next: ({ data }) => {
        if (data) {
          this.itemsMasterSelected.set(data);
        }
      },
      error: (error) => {
        console.error('Error fetching item masters:', error);
      },
    });
  }

  private populateFormArray(items: ItemCodeKeyWithResult[]) {
    this.itemCodeKeyWithResults.clear();

    items.forEach((item) => {
      const group = this.createFormIntoTable(item);

      const resultStatusCtrl = group.get('resultStatus');
      const defectCtrl = group.get('qualityDefectReference');

      // 🔹 Cuando el usuario cambia un campo, replicamos el valor en los demás
      resultStatusCtrl?.valueChanges.subscribe((val) =>
        this.syncGroupChanges(item.manufacturerLotNumber, 'resultStatus', val, resultStatusCtrl),
      );

      defectCtrl?.valueChanges.subscribe((val) =>
        this.syncGroupChanges(item.manufacturerLotNumber, 'qualityDefectReference', val, defectCtrl),
      );

      this.itemCodeKeyWithResults.push(group);
      this.ctr.detectChanges();
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private syncGroupChanges(lotNumber: string, controlName: string, value: any, sourceCtrl: AbstractControl) {
    this.itemCodeKeyWithResults.controls.forEach((ctrl) => {
      if (ctrl === sourceCtrl) return;

      if (ctrl.get('manufacturerLotNumber')?.value === lotNumber) {
        const targetCtrl = ctrl.get(controlName);
        if (targetCtrl && targetCtrl.value !== value) {
          targetCtrl.setValue(value, { emitEvent: false });
        }
      }
    });
  }

  invoiceOptions = signal<Invoice[]>([]);

  itemMasterOptions = signal<ItemMaster[]>([]);

  public fetchInvoices = (query: string): Observable<Invoice[]> => {
    const invoiceCommand: InvoiceSearchCommand = {
      invoiceNumber: query,
      excludeInvoicesInShipment: false,
    };

    return this.invoiceProxyService.searchInvoice(invoiceCommand, 1, 9999).pipe(map(({ data }) => data?.items ?? []));
  };

  private getShipment(invoiceId: string): void {
    this.isLoading++;
    this.shipmentProxyService.getShipmentByInvoiceReference(invoiceId).subscribe({
      next: ({ data }) => {
        if (data) {
          this.shipmentSelected.setValue(data);

          this.isLoading--;
        }
      },
      error: (error) => {
        this.isLoading--;
        this.invoiceSelected.setValue(null);
        console.error('Error fetching shipment:', error);
      },
    });
  }

  private getProviders(): void {
    const businessSearch: BusinessSearch = {
      name: '',
      code: '',
      businessTypes: [0],
    };
    this.financeProxyService.getAllProviders(businessSearch).subscribe({
      next: ({ data }) => {
        if (data) {
          this.isLoading--;
          this.providers.set(data);
        }
      },
      error: (error) => {
        this.isLoading--;
        this.providers.set([]);
        console.error('Error fetching providers:', error);
      },
    });
  }

  private getManufacturers(): void {
    const businessSearch: BusinessSearch = {
      name: '',
      code: '',
      businessTypes: [2],
    };
    this.financeProxyService.getAllManufacturers(businessSearch).subscribe(
      ({ data }) => {
        if (data?.items) {
          this.isLoading--;
          this.manufacturers.set(data.items || []);
        }
      },
      (error) => {
        this.isLoading--;
        this.manufacturers.set([]);
        console.error('Error fetching manufacturers:', error);
      },
    );
  }

  private getLotsByInvoiceReference(invoiceReference: string): void {
    this.isLoading++;
    this.lotItemProxyService.getLotsByInvoiceId(invoiceReference).subscribe({
      next: ({ data }) => {
        if (data) {
          // 🔹 Guardamos todos los lotes para referencia
          this.lotsSelected.set(data);

          // 🔹 Limpiamos el form array
          this.itemCodeKeyWithResults.clear();

          // 🔹 Creamos un FormGroup por cada lote recibido
          data.forEach((lot) => {
            const formGroup = this.createFormIntoTable({
              id: lot.id,
              itemCode: lot.itemCode,
              manufacturerLotNumber: lot.providerLotId,
              resultStatus: 0,
              changedBy: this.userLite?.username || '',
              dateModified: new Date(),
            });
            this.itemCodeKeyWithResults.push(formGroup);
          });
        }
        this.isLoading--;
      },
      error: (error) => {
        this.isLoading--;
        this.lotsSelected.set([]);
        console.error('Error fetching lots:', error);
      },
    });
  }

  private createFormIntoTable(item: ItemCodeKeyWithResult): FormGroup<ItemMasterWithResults> {
    return new FormGroup<ItemMasterWithResults>({
      id: new FormControl(item.id || null),
      itemCode: new FormControl(item.itemCode || null),
      manufacturerLotNumber: new FormControl(item.manufacturerLotNumber || null),
      resultStatus: new FormControl(item.resultStatus ?? null),
      changedBy: new FormControl(item.changedBy || ''),
      dateModified: new FormControl(item.dateModified || new Date()),
      workOrderReference: new FormControl(item.workOrderReference || null),
      colorReferences: new FormControl(item.colorReferences || null),
      qualityDefectReference: new FormControl(item.qualityDefectReference || null),
      comments: new FormControl([]),
      supportingDocuments: new FormControl([]),
    });
  }

  createYarnTestedForm(): FormGroup {
    return new FormGroup({
      yarnCode: new FormControl(''),
      description: new FormControl(''),
      provider: new FormControl(''),
      manufacturer: new FormControl(''),
    });
  }

  /**
   * The options come from the selected invoice's purchase order, not from the typed text — the field is a
   * picker over a fixed set. It still goes through the directive so the keystroke burst collapses into
   * one request instead of one per character.
   */
  public fetchItemMasters = (): Observable<ItemMaster[]> => {
    const itemsToSearch = this.invoiceSelected.value?.purchaseOrder?.items.flatMap((item) => item.itemCode);
    if (!itemsToSearch?.length) return of([]);

    return this.itemMasterProxyService.getByItemCodes(itemsToSearch).pipe(
      map(({ data }) => data ?? []),
      catchError((error) => {
        console.error('Error fetching items:', error);
        return of([] as ItemMaster[]);
      }),
    );
  };

  selectedItemMaster(event: AutoCompleteSelectEvent) {
    const invoice = this.invoiceSelected.value;
    if (!invoice) return;

    // 1. Chequear si ya está agregado
    const exists = this.itemsMasterSelected().some((item) => item.itemCode === event.value.itemCode);
    if (exists) return;

    // 2. Buscar el item en el PurchaseOrder
    const poItem = invoice.purchaseOrder?.items.find((i) => i.itemCode === event.value.itemCode);
    if (!poItem) return;

    // 3. Buscar el item dentro de la invoice que referencia al PO item
    const invoiceItem = invoice.items.find((ii) => ii.itemInPurchaseOrderId === poItem.id);

    // 4. Armar un objeto extendido
    const newItem = {
      ...event.value, // datos del ItemMaster
      providerRef: invoice.purchaseOrder?.providerReference,
      manufacturerRef: invoiceItem?.manufacturerReference,
    };

    // 5. Guardarlo en el signal
    this.itemsMasterSelected.update((items) => [...items, newItem]);
  }

  getProvider(): string {
    const result = this.invoiceSelected.value?.purchaseOrder?.providerReference;

    return this.providers().find((provider) => provider.id === result)?.name ?? '----';
  }

  getManufacturer(itemCode: string): string {
    const idItem = this.invoiceSelected.value?.purchaseOrder?.items.find((item) => item.itemCode === itemCode)?.id;

    const manufactureMatchRef = this.invoiceSelected.value?.items.find(
      (item) => item.itemInPurchaseOrderId === idItem,
    )?.manufacturerReference;

    const manufacturer = this.manufacturers().find((manufacturer) => manufacturer.id === manufactureMatchRef);

    return manufacturer ? manufacturer.name : '----';
  }

  private resultStatusType(): void {
    const object = this.enumService.getEnum('ResultStatusType', 'inventory');

    this.resultStatusTypeOptions.set(setOptionsForPrimeNg(object.values));
  }

  //#endregion

  onSave() {
    if (!this.invoiceSelected.value) {
      GlobalMessageUtil.error('Error', 'Please select an invoice');
      return;
    }

    if (this.itemCodeKeyWithResults.length === 0) {
      GlobalMessageUtil.error('Error', 'Please add at least one item result');
      return;
    }

    if (this.itemCodeKeyWithResults.length <= 0) {
      GlobalMessageUtil.error('Error', 'Please add at least one item result');
      return;
    }

    // 🔹 Paso extra: sincronizar valores entre lotes antes de armar el payload
    for (const lot of this.uniqueLotGroups()) {
      const refValues = lot.group.value;

      this.itemCodeKeyWithResults.controls
        .filter((ctrl) => ctrl.get('manufacturerLotNumber')?.value === lot.lotNumber)
        .forEach((ctrl) => {
          ctrl.patchValue(
            {
              resultStatus: refValues.resultStatus,
              qualityDefectReference: refValues.qualityDefectReference,
              comments: refValues.comments,
              supportingDocuments: refValues.supportingDocuments,
            },
            { emitEvent: false },
          );
        });
    }

    const dataToSend: YarnQC = {
      number: this.yarnQC()?.number || undefined,
      auditorUsername: this.userLite?.username || 'user-tester',
      controlledRolls: this.yarnQC()?.controlledRolls || [],
      createdBy: this.userLite?.username || 'user-tester',
      createdDate: this.isUpdate() ? undefined : new Date(),
      comments: this.conditional.value ?? '',
      id: this.isUpdate() ? this.yarnQC()?.id : undefined,
      invoiceReference: this.invoiceSelected.value.id,
      itemCodeKeyWithResults: this.itemCodeKeyWithResults.getRawValue() as ItemCodeKeyWithResult[],
      modifiedBy: this.isUpdate() ? this.userLite?.username : undefined,
      modifiedDate: this.isUpdate() ? new Date() : undefined,
      notes: this.notes,
      supportingDocuments: this.generalFiles() || [],
    };

    this.isLoading++;
    if (!this.isUpdate()) {
      this.yarnQcProxyService.createYarnQC(dataToSend).subscribe({
        next: (res) => {
          if (res.data) {
            this.isLoading--;
            GlobalMessageUtil.success('Success', 'Yarn QC created successfully');
            GlobalMessageUtil.warn('Note', 'You can upload files now ');
            this.router.navigate(['/yarn-qc-details'], { queryParams: { id: res.data.id } });
            this.ctr.detectChanges();
          }
        },
        error: (error) => {
          this.isLoading--;
          console.error('Error:', error);
        },
      });
    } else {
      this.yarnQcProxyService.updateYarnQC(dataToSend).subscribe({
        next: () => {
          this.isLoading--;
          GlobalMessageUtil.success('Success', 'Yarn QC updated successfully');
          this.router.navigate(['/yarns-qc']);
        },
        error: (error) => {
          this.isLoading--;
          console.error('Error:', error);
        },
      });
    }
  }

  cancel() {
    this.resetData();
    this.router.navigate(['/yarns-qc']);
  }

  resetData() {
    this.shipmentSelected.setValue(null);
    this.itemMasterOptions.set([]);
    this.itemsMasterSelected.set([]);
    this.lotsSelected.set([]);
    this.yarnToTestedSelected.setValue(null);
    this.generalFiles.set([]);
  }

  openFile(url: string) {
    window.open(url, '_blank');
  }
  onViewMore(): void {
    this.openNotesModal();
  }

  openNotesModal(): void {
    const modalData = {
      notes: this.notes,
      userLite: this.userLite,
      viewOnly: false,
    };

    this.modalService.open(NotesSidebarComponent, {}, modalData).subscribe({
      next: (result) => {
        if (result && result.action === 'noteAdded') {
          this.notes = result.data.notes;
        }
      },
      error: (error) => {
        console.error('Error handling notes modal result:', error);
      },
    });
    setTimeout(() => {
      const modalElement = document.querySelector('.panel.modal') as HTMLElement;
      if (modalElement) modalElement.style.maxWidth = '50%';
      modalElement.style.width = '50%';
    }, 100);
  }
}
